// Language-to-metadata mapping for homepage
export const languageMetadata = {
  es: {
    name: "Spanish",
    title: "Learn Spanish with Games – Vocabulary, Grammar, Speaking & More | Conjugr8",
    description: "Master Spanish with Conjugr8, the language learning app built around games. Practice vocabulary, grammar, sentences, speaking, and comprehension to learn Spanish faster.",
    keywords: "learn spanish app, spanish vocabulary games, spanish grammar practice, spanish speaking practice, spanish comprehension, spanish sentence builder, best app to learn spanish"
  },
  fr: {
    name: "French", 
    title: "Learn French with Games – Vocabulary, Grammar, Speaking & More | Conjugr8",
    description: "Improve your French with Conjugr8's language games. Practice vocabulary, grammar, sentence building, speaking, and comprehension with fun, interactive exercises.",
    keywords: "learn french app, french vocabulary games, french grammar practice, french speaking app, french comprehension practice, learn french sentences"
  },
  it: {
    name: "Italian",
    title: "Learn Italian with Games – Vocabulary, Grammar, Speaking & More | Conjugr8", 
    description: "Play fun games to learn Italian fast. Conjugr8 helps you practice Italian vocabulary, grammar, sentences, speaking, and comprehension in one language learning app.",
    keywords: "learn italian app, italian vocabulary games, italian grammar practice, italian speaking practice, italian comprehension app, learn italian sentences"
  },
  pt: {
    name: "Portuguese",
    title: "Learn Portuguese with Games – Vocabulary, Grammar, Speaking & More | Conjugr8",
    description: "Practice Portuguese through fun language games. Build your vocabulary, improve grammar, practice speaking, and strengthen comprehension with Conjugr8.",
    keywords: "learn portuguese app, portuguese vocabulary games, portuguese grammar practice, portuguese speaking practice, portuguese comprehension exercises"
  },
  ca: {
    name: "Catalan",
    title: "Learn Catalan with Games – Vocabulary, Grammar, Speaking & More | Conjugr8",
    description: "Master Catalan step by step with Conjugr8. Play vocabulary, grammar, sentence, and speaking games to improve your skills and comprehension.",
    keywords: "learn catalan app, catalan vocabulary games, catalan grammar practice, catalan speaking practice, catalan comprehension app"
  },
  ro: {
    name: "Romanian",
    title: "Learn Romanian with Games – Vocabulary, Grammar, Speaking & More | Conjugr8",
    description: "Conjugr8 makes Romanian fun to learn with interactive games. Practice vocabulary, grammar, speaking, sentence building, and comprehension in one place.",
    keywords: "learn romanian app, romanian vocabulary games, romanian grammar practice, romanian speaking app, romanian comprehension exercises"
  },
  fi: {
    name: "Finnish",
    title: "Learn Finnish with Games – Vocabulary, Grammar, Speaking & More | Conjugr8",
    description: "Start learning Finnish with Conjugr8. Practice Finnish vocabulary, grammar, sentences, speaking, and comprehension with interactive exercises.",
    keywords: "learn finnish app, finnish vocabulary games, finnish grammar practice, finnish speaking practice, finnish comprehension exercises, learn finnish online"
  }
};

export function getLanguageMetadata(currentLanguage: string) {
  return languageMetadata[currentLanguage as keyof typeof languageMetadata] || languageMetadata.es;
}

// Page-specific metadata templates
export const pageMetadataTemplates = {
  vocabulary: {
    title: "[Language] Vocabulary Games – Learn Words Fast with Flashcards & Quizzes",
    description: "Boost your [Language] vocabulary with fun games. Play flashcards, word quizzes, and memory challenges to learn new words quickly and retain them longer.",
    keywords: "[language] vocabulary game, [language] flashcards, [language] vocabulary app, learn [language] words, practice [language] vocabulary"
  },
  grammar: {
    title: "[Language] Grammar Games – Practice Rules with Interactive Exercises",
    description: "Master [Language] grammar through interactive games. Practice sentence structure, articles, prepositions, and grammar rules with fun, bite-sized challenges.",
    keywords: "[language] grammar practice, [language] grammar game, [language] grammar app, learn [language] grammar rules, practice [language] grammar"
  },
  sentences: {
    title: "[Language] Sentence Games – Build and Practice Sentences with Fun Quizzes",
    description: "Learn how to build sentences in [Language]. Practice word order, verb placement, and sentence structure with engaging, interactive games and quizzes.",
    keywords: "[language] sentence practice, [language] sentence game, [language] sentence builder, learn [language] sentences, practice [language] word order"
  },
  speaking: {
    title: "[Language] Speaking Games – Practice Pronunciation & Fluency",
    description: "Improve your [Language] speaking skills with interactive games. Practice pronunciation, fluency, and real-world dialogues using voice exercises and challenges.",
    keywords: "[language] speaking practice, [language] speaking game, [language] pronunciation app, practice speaking [language], learn [language] conversation"
  },
  comprehension: {
    title: "[Language] Comprehension Games – Improve Listening & Reading Skills",
    description: "Enhance your [Language] comprehension with games. Practice listening, reading, and understanding sentences through fun, story-based challenges and quizzes.",
    keywords: "[language] comprehension practice, [language] listening game, [language] reading comprehension, practice [language] understanding, learn [language] listening"
  }
};

const langNameMap: Record<string, string> = {
  es: 'spanish', pt: 'portuguese', fr: 'french', it: 'italian', ca: 'catalan', ro: 'romanian', fi: 'finnish'
};

export function getCanonicalUrl(urlPath: string | undefined, languageCode: string): string {
  const langName = langNameMap[languageCode] || 'spanish';
  if (!urlPath) return `https://www.conjugr8.com/learn-${langName}`;
  if (urlPath.match(/^\/[a-z]+-practice\/?$/)) {
    return `https://www.conjugr8.com/${langName}-practice`;
  }
  if (urlPath === '/' || urlPath.match(/^\/learn-[a-z]+\/?$/)) {
    return `https://www.conjugr8.com/learn-${langName}`;
  }
  const subPath = urlPath.replace(/^\/learn-[a-z]+/, '');
  return `https://www.conjugr8.com/learn-${langName}${subPath}`;
}

export function getPageMetadata(currentLanguage: string, pageType: keyof typeof pageMetadataTemplates) {
  const languageMeta = getLanguageMetadata(currentLanguage);
  const template = pageMetadataTemplates[pageType];
  
  return {
    title: template.title.replace(/\[Language\]/g, languageMeta.name),
    description: template.description.replace(/\[Language\]/g, languageMeta.name),
    keywords: template.keywords.replace(/\[language\]/g, languageMeta.name.toLowerCase())
  };
}
